<template>
  <OnboardingPage v-if="!skipOnboarding && user.onboarded_steps_completed < 5" />
  <div v-else class="h-screen flex relative overflow-hidden">
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog
        as="div"
        class="fixed inset-0 flex z-40 md:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div class="relative flex-1 flex flex-col max-w-xs w-full bg-gray-900">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">{{ $t('layout.dashboard.close_sidebar') }}</span>
                  <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <router-link to="/" class="flex-shrink-0 flex items-center px-4 cursor-pointer">
                <img class="h-8 w-auto" :src="Logo" :alt="$t('layout.dashboard.logo_alt')" />
              </router-link>
              <nav class="mt-5 px-2 space-y-1">
                <router-link
                  v-for="item in navigation.filter((navItem) => !navItem.disabled)"
                  :key="item.name"
                  :to="getRoute(item)"
                  :class="[
                    item.current
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                    'group flex items-center px-2 py-2 text-base font-light rounded-md',
                  ]"
                >
                  <img
                    :src="item.icon"
                    class="mr-4 flex-shrink-0 h-6 w-6 gameIcon"
                    :class="[
                      item.current
                        ? 'brightness-200'
                        : '']"
                    v-if="item.customIcon"
                  />
                  <component
                    :is="item.icon"
                    :class="[
                      item.current
                        ? 'text-gray-300'
                        : 'text-gray-400 group-hover:text-gray-300',
                      'mr-4 flex-shrink-0 h-6 w-6',
                    ]"
                    aria-hidden="true"
                    v-else
                  />
                  <span class="flex-1">{{ item.name }}</span>
                  <MatchmakingNavStatus :status="navStatus(item)" />
                </router-link>
                <a
                  v-for="item in externalNavigation"
                  :key="item.name"
                  :href="item.href"
                  target="_blank"
                  :class="[
                    item.current
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                    'group flex items-center px-2 py-2 text-base font-light rounded-md',
                  ]"
                >
                  <img
                    :src="item.icon"
                    class="mr-4 flex-shrink-0 h-6 w-6"
                    :class="[
                      item.current
                        ? 'brightness-200'
                        : '']"
                    v-if="item.customIcon"
                  />
                  <component
                    :is="item.icon"
                    :class="[
                      item.current
                        ? 'text-gray-300'
                        : 'text-gray-400 group-hover:text-gray-300',
                      'mr-4 flex-shrink-0 h-6 w-6',
                    ]"
                    aria-hidden="true"
                    v-else
                  />
                  {{ item.name }}
                </a>
              </nav>
            </div>
          </div>
        </TransitionChild>
        <div class="flex-shrink-0 w-14">
          <!-- Force sidebar to shrink to fit close icon -->
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Collapsed sidebar for desktop -->
    <div
      v-if="iconsOnly"
      @mouseover="sideBarExtended = true"
      @mouseleave="sideBarExtended = false"
      class="flex"
    >
      <div
        class="md:w-16 md:hover:w-[210px] grow-transition hidden md:flex md:flex-col md:fixed md:inset-y-0 z-20"
      >
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex-1 flex flex-col min-h-0 bg-gray-900 boxshadow-r">
          <div class="flex-1 flex flex-col pt-6 overflow-y-auto overflow-x-hidden">
            <router-link to="/" class="flex items-center pb-12 pl-6">
              <img v-if="sideBarExtended" class="min-w-[152px] overflow-hidden" :src="Logo" :alt="$t('layout.dashboard.logo_alt')" />
              <img v-else class="h-6" src="../../assets/logos/refrag-icon.svg" :alt="$t('layout.dashboard.logo_alt')" />
            </router-link>
            <div
              class="text-xs text-text-secondary uppercase tracking-[1.5px] pl-6"
              :class="[sideBarExtended ? 'pb-2.5' : 'pb-[26px]']">
              <span v-if="sideBarExtended">{{ $t('common.overview') }}</span>
            </div>
            <nav class="flex-1 space-y-1">
              <router-link
                v-for="item in navigation.filter((navItem) => !navItem.disabled)"
                :key="item.name"
                :to="getRoute(item)"
                :class="[
                  item.current
                    ? 'bg-gray-800 selected-nav text-text-primary'
                    : 'hover:bg-gray-700 text-text-secondary',
                  'group relative flex items-center py-2 text-sm  font-medium rounded-r-md mr-4 whitespace-nowrap',
                ]"
              >
                <img
                  :src="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  :class="[
                    item.current
                      ? 'brightness-200'
                      : '']"
                  v-if="item.customIcon"
                />
                <component
                  :is="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  aria-hidden="true"
                  v-else
                />
                <span v-if="sideBarExtended" class="flex-1">{{ item.name }}</span>
                <Pill class="px-1 py-[1px] mr-1" v-if="item.showUpgrade && sideBarExtended" :text="$t('common.upgrade')" bgColor="#EF6B6B20" textColor="#EF6B6B" />
                <Pill class="px-1 py-[1px] mr-1" v-if="item.beta && sideBarExtended && !item.showUpgrade && !navStatus(item)" :text="$t('layout.dashboard.beta')" bgColor="#D071C520" textColor="#D071C5" />
                <Pill class="px-1 py-[1px] mr-1" v-if="item.new && sideBarExtended && !item.showUpgrade" :text="$t('layout.dashboard.new')" bgColor="#14B8A620" textColor="#14B8A6" />
                <!-- Collapsed, the row is clipped to the icon, so the status rides on the icon instead -->
                <MatchmakingNavStatus
                  v-if="sideBarExtended"
                  :status="navStatus(item)"
                  class="mr-3"
                />
                <MatchmakingNavStatus
                  v-else
                  :status="navStatus(item)"
                  compact
                  class="absolute left-[38px] top-1"
                />
              </router-link>
              <a
                v-for="item in externalNavigation"
                :key="item.name"
                :href="item.disabled ? '#' : item.href"
                target="_blank"
                :class="[
                  item.current
                    ? 'bg-gray-800 border-l-2 border-primary-600 text-text-primary'
                    : 'hover:bg-gray-700 text-text-secondary',
                  'group flex items-center py-2 text-sm text-text-primary font-medium rounded-r-md mr-4 whitespace-nowrap',
                ]"
              >
                <img
                  v-if="item.customIcon"
                  :src="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  aria-hidden="true"
                />
                <component
                  v-else
                  :is="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  aria-hidden="true"
                />
                <span v-if="sideBarExtended">{{ item.name }}</span>
              </a>
            </nav>
          </div>
          <div
            class="flex-shrink-0 flex border-t border-gray-700 py-6"
            :class="[sideBarExtended ? 'mx-6' : 'mx-2']">
            <Menu as="div" class="relative inline-block text-left w-full">
              <div>
                <MenuButton
                  class="group w-full rounded-md text-left text-sm font-medium text-gray-700 hover:bg-[#131A20] focus:outline-none focus:ring-0"
                  :class="[sideBarExtended ? 'px-3.5 py-2 border border-gray-700 bg-gray-800' : '']"
                >
                  <span
                    class="flex w-full items-center"
                    :class="[sideBarExtended ? 'justify-between' : 'justify-center']">
                    <span class="flex min-w-0 items-center justify-between space-x-3">
                      <img
                        class="h-6 w-6 flex-shrink-0 rounded-full"
                        :src="activeTeam.logo || buttonLogo"
                        alt=""
                      />
                      <span v-if="sideBarExtended" class="flex min-w-0 flex-1 flex-col">
                        <span class="truncate text-sm font-medium text-text-secondary">{{
                          activeTeam.name
                        }}</span>
                      </span>
                    </span>
                    <img
                      v-if="sideBarExtended"
                      src="../../assets/icons/dropdown.svg"
                      class="flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                  </span>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="-top-2 transform -translate-y-full absolute right-0 w-full origin-top-right divide-y divide-gray-100 rounded-md border border-gray-700 shadow-lg outline-none bg-gray-900"
                >
                  <div class="py-1">
                    <MenuItem v-for="team in user.teams" :key="team.id">
                      <a
                        href="#"
                        @click="updateActiveTeam(team)"
                        :class="[
                          activeTeam.id == team.id
                            ? 'text-text-primary bg-gray-700'
                            : 'text-text-secondary bg-gray-900 hover:text-text-primary hover:bg-gray-700',
                          'block px-4 py-2 text-sm',
                        ]"
                      >{{ team.name }}</a
                      >
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div v-else class="hidden md:flex md:w-[210px] md:flex-col md:fixed md:inset-y-0 z-20">
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex-1 flex flex-col min-h-0 bg-gray-900 boxshadow-r">
        <div class="flex-1 flex flex-col pt-6 overflow-y-auto">
          <router-link to="/" class="flex items-center flex-shrink-0 pb-12 pl-6">
            <img class="w-[152px]" :src="Logo" :alt="$t('layout.dashboard.logo_alt')" />
          </router-link>
          <div class="text-xs text-text-secondary uppercase pb-2.5 tracking-[1.5px] pl-6">
            {{ $t('common.overview') }}
          </div>
          <nav class="flex-1 space-y-1">
            <div class="flex flex-col gap-2" v-for="item in navigation.filter((navItem) => !navItem.disabled)">
              <router-link
                :key="item.name"
                :to="getRoute(item)"
                :class="[
                  item.current
                    ? 'bg-gray-800 selected-nav text-text-primary'
                    : 'hover:bg-gray-700 text-text-secondary',
                  'group flex items-center py-2 text-sm  font-medium rounded-r-md mr-4 whitespace-nowrap',
                ]"
              >
                <img
                  :src="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  :class="[
                    item.current
                      ? 'brightness-200'
                      : '']"
                  v-if="item.customIcon"
                />
                <component
                  :is="item.icon"
                  class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                  aria-hidden="true"
                  v-else
                />
                <p class="flex-1">{{ item.name }}</p>
                <Pill class="px-1 py-[1px] mr-1" v-if="item.showUpgrade" :text="$t('common.upgrade')" bgColor="#EF6B6B20" textColor="#EF6B6B" />
                <Pill class="px-1 py-[1px] mr-1" v-if="item.beta && !item.showUpgrade && !navStatus(item)" :text="$t('layout.dashboard.beta')" bgColor="#D071C520" textColor="#D071C5" />
                <Pill class="px-1 py-[1px] mr-1" v-if="item.new && !item.showUpgrade" :text="$t('layout.dashboard.new')" bgColor="#14B8A620" textColor="#14B8A6" />
                <MatchmakingNavStatus :status="navStatus(item)" class="mr-3" />
              </router-link>
              <div v-if="item.subMenu && item.current" class="w-full px-8 flex gap-4">
                <div class="w-[2px] bg-gray-800">
                  <div
                    class="w-[2px] h-6 bg-primary-600 transition-all duration-300"
                    :style="{ marginTop: subMenuSliderMargin || 0 }" />
                </div>
                <div class="flex flex-col flex-1 gap-2">
                  <router-link
                    v-for="subItem in item.subMenu"
                    :to="subItem.href"
                    class="w-full py-1 flex items-center text-xs"
                    :class="[subItem.current ? 'text-text-primary' : 'text-text-secondary']">
                    {{ subItem.name }}
                  </router-link>
                </div>
              </div>
            </div>
            <a
              v-for="item in externalNavigation"
              :key="item.name"
              :href="item.disabled ? '#' : item.href"
              target="_blank"
              :class="[
                item.current
                  ? 'bg-gray-800 border-l-2 border-primary-600 text-text-primary'
                  : 'hover:bg-gray-700 text-text-secondary',
                'group flex items-center py-2 text-sm text-text-primary font-medium rounded-r-md mr-4 whitespace-nowrap',
              ]"
            >
              <img
                v-if="item.customIcon"
                :src="item.icon"
                class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
              />
              <component
                v-else
                :is="item.icon"
                class="mr-3 ml-6 flex-shrink-0 h-5 w-5"
                aria-hidden="true"
              />
              {{ item.name }}
            </a>
          </nav>
        </div>

        <div v-if="!userCheckListCompleted" class="p-4">
          <Menu as="div" class="relative inline-block text-left w-full">
            <div>
              <MenuButton
                class="group bg-gray-950 rounded-lg w-full border px-3 py-2 border-gray-800 text-left hover:bg-gray-800 duration-300 focus:outline-none focus:ring-0"
              >
                <span class="flex w-full items-center justify-between">
                  <div class="z-10 relative w-10 h-10">
                    <svg class="w-full h-full" viewBox="0 0 100 100">
                      <!-- Background circle -->
                      <circle
                        class="text-gray-700 stroke-current"
                        stroke-width="5"

                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                      />
                      <!-- Progress circle -->
                      <circle
                        class="text-indigo-500 progress-ring__circle stroke-current"
                        stroke-width="8"
                        stroke-linecap="round"
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke-dasharray="251.33"
                        :stroke-dashoffset="251.33 - (userCompletedTasks.length / 5 * 251.33)"
                      />
                    </svg>
                    <div class="absolute inset-0 flex items-baseline justify-center mt-3">
                      <span class="text-text-primary font-medium text-xs">{{ userCompletedTasks.length }}</span>
                      <span class="text-text-secondary text-xs">/{{ userCheckListProgress.length }} </span>
                    </div>
                  </div>
                  <h1 class="text-sm text-text-primary">{{ $t('layout.dashboard.get_started') }}</h1>
                  <img
                    src="../../assets/icons/dropdown.svg"
                    class="flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="bg-gray-950 p-4 rounded-lg min-w-[300px] border-gray-800 -top-2 left-2 transform -translate-y-full absolute right-0 w-full origin-top-right divide-y divide-gray-100 rounded-md border shadow-lg"
              >
                <div class="flex flex-col gap-4 text-sm">
                  <MenuItem v-for="task in userCompletedTasks" :key="task.step">
                    <div class="flex gap-3 items-start">
                      <div class="flex items-center rounded-full bg-primary-500 p-1">
                        <CheckIcon class="w-3 h-3 text-text-primary" />
                      </div>
                      <div class="flex flex-col gap-2 pr-6">
                        <h1 class="text-text-secondary text-sm line-through">{{ task.title }}</h1>
                        <p class="text-xs text-text-secondary line-through">
                          {{ task.description }}
                        </p>
                      </div>
                    </div>
                  </MenuItem>
                  <MenuItem v-for="task in userUncompletedTasks" :key="task.step" class="cursor-pointer">
                    <router-link :to="task.route" class="flex gap-3 items-start hover:bg-gray-800 rounded-lg p-1 -m-1 duration-300">
                      <div class="flex items-center rounded-full border border-gray-500 min-w-5 min-h-5" />
                      <div class="flex flex-col gap-2 flex-1">
                        <h1 class="text-text-primary text-sm">{{ task.title }}</h1>
                        <p class="text-xs text-text-primary">
                          {{ task.description }}
                        </p>
                      </div>
                      <img src="../../assets/icons/rightarrow.svg" class="w-4 h-4" />
                    </router-link>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>

        <div class="flex-shrink-0 flex border-t border-gray-700 bg-gray-950">
          <Menu as="div" class="relative inline-block text-left w-full">
            <div>
              <MenuButton
                class="group w-full border px-3.5 py-4 border-none text-left hover:bg-gray-800 duration-300 focus:outline-none focus:ring-0"
              >
                <span class="flex w-full items-center justify-between">
                  <span class="flex min-w-0 items-center justify-between space-x-2">
                    <img
                      class="h-6 w-6 flex-shrink-0 rounded-full bg-gray-300"
                      :src="activeTeam.logo || buttonLogo"
                      alt=""
                    />
                    <span class="flex min-w-0 flex-1 flex-col">
                      <span class="truncate text-sm text-text-primary">{{
                        activeTeam.name
                      }}</span>
                    </span>
                    <Pill class="px-1" :text="getTeamTierInfo(activeTeam.access_level).text" :bgColor="getTeamTierInfo(activeTeam.access_level).bgColor" :textColor="getTeamTierInfo(activeTeam.access_level).textColor" />
                  </span>
                  <img
                    src="../../assets/icons/dropdown.svg"
                    class="flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="bg-gray-950 z-20 rounded-lg border-gray-800 -top-2 left-2 transform -translate-y-full absolute right-0 w-full origin-top-right divide-y divide-gray-100 rounded-md border shadow-lg"
              >
                <div class="">
                  <MenuItem v-for="team in user.teams" :key="team.id">
                    <a
                      href="#"
                      @click="updateActiveTeam(team)"
                      class="flex gap-2 hover:bg-gray-800 duration-300 items-center px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
                    >
                      <img :src="team.logo || buttonLogo" class="h-6 w-6 flex-shrink-0 rounded-full" />
                      <h1 class="text-text-primary text-sm whitespace-nowrap overflow-hidden text-ellipsis">{{ team.name }}</h1>
                      <Pill class="px-1" :text="getTeamTierInfo(team.access_level).text" :bgColor="getTeamTierInfo(team.access_level).bgColor" :textColor="getTeamTierInfo(team.access_level).textColor" />
                      <div class="flex-1" />
                      <img v-if="activeTeam.id == team.id" src="../../assets/icons/check.svg" class="w-3 h-3" />
                    </a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
    <div :class="[iconsOnly ? 'md:pl-16' : 'md:pl-[210px]', 'flex flex-col min-w-full flex-1 h-full']">
      <main class="h-full flex flex-col bg-gray-950 overflow-hidden">
        <div class="flex-shrink-0 z-10">
          <NavBar
            v-bind:toggleMobileMenu="toggleMobileMenu"
            :user="user"
            :pin-profile-sidebar="pinProfileSidebar && !!user?.can_use_matchmaking"
            @openUpgradeModal="showUpgradeModal = true"
          />
        </div>

        <div v-if="noPadding" class="flex-1 flex z-0 overflow-hidden">
          <!-- Replace with your content -->
          <div class="flex flex-1 overflow-y-auto" v-if="$auth.ready()">
            <AlertPopup />
            <slot />
          </div>
          <div v-else>{{ $t('layout.dashboard.loading') }}</div>
          <!-- /End replace -->
        </div>

        <div v-else class="flex-1 z-0 overflow-hidden" :class="pinProfileSidebar && user?.can_use_matchmaking ? 'xl:pr-[240px]' : ''">
          <div class="mx-auto overflow-y-auto h-full">
            <!-- Replace with your content -->
            <div class="p-4 md:p-12" v-if="$auth.ready()">
              <AlertPopup />
              <slot />
            </div>
            <div v-else>{{ $t('layout.dashboard.loading') }}</div>
            <!-- /End replace -->
          </div>
        </div>

      </main>
    </div>
    <div v-if="showNotifications" class="flex items-center justify-center w-full h-full fixed inset-y-0 bg-black/60 z-50">
      <NotificationsPopup :data="notifications[0]" @close="seeNotification" />
    </div>
    <SubscribeModal
      :show="showUpgradeModal"
      @closeModal="showUpgradeModal = false"
    />
    <MatchmakingBlock />
  </div>

</template>

<script>
import {
  Dialog,
  DialogOverlay,
  TransitionChild,
  TransitionRoot,
  Menu,
  MenuItem,
  MenuButton,
  MenuItems,
} from '@headlessui/vue';
import {
  Bars3Icon,
  ShoppingCartIcon,
  XMarkIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  VideoCameraIcon,
  UserIcon,
  FilmIcon,
  Bars4Icon,
  CloudIcon,
  ChartBarIcon,
  XCircleIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  UserGroupIcon,
  ChevronUpIcon,
  StarIcon,
  PlayIcon,
  CheckIcon,
} from '@heroicons/vue/20/solid';
import { useI18n } from 'vue-i18n';
import { routerKey, useRoute, useRouter } from 'vue-router';
import { ref, computed, watch } from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useHead } from '@unhead/vue';
import { useGtm } from '@gtm-support/vue-gtm';
import NavBar from './NavBar.vue';
import NotificationsPopup from '../ImportantNotificationPopupModal.vue';
import Logo from '../../utils/logo';
import buttonLogo from '../../assets/logos/refrag-icon-button.svg';
import { clearSignedOutUser } from '../../utils/session';
import useAppShell from '../../composables/useAppShell';
import AlertPopup from '../AlertPopup.vue';
import RestratIcon from '../../assets/icons/restrat.svg';
import AcademyIcon from '../../assets/icons/academy.svg';
import TrainingSessionsIcon from '../../assets/icons/training_sessions.svg';
import CoachIcon from '../../assets/icons/coach.svg';
import HomeIcon from '../../assets/icons/home.svg';
import GlobeAmericasIcon from '../../assets/icons/globe.svg';
import UtilityIcon from '../../assets/icons/utility.svg';
import LeaderboardIcon from '../../assets/icons/leaderboard.svg';
import LibraryIcon from '../../assets/icons/library.svg';
import WikiIcon from '../../assets/icons/wiki.svg';
import SupportIcon from '../../assets/icons/support.svg';
import ServerIcon from '../../assets/icons/server.svg';
import SteamConnectModal from '../SteamConnectModal.vue';
import SmokeIcon from '../../assets/icons/smoke.svg';
import Pill from '../Pill.vue';
import OnboardingPage from '../../views/OnboardingPage.vue';
import BootcampIcon from '../../assets/icons/bootcamp.svg';
import RoutinesIcon from '../../assets/icons/routines.svg';
import MatchmakingIcon from '../../assets/icons/matchmaking.svg';
import MatchmakingBlock from '../Matchmaking/MatchmakingBlock.vue';
import MatchmakingNavStatus from '../Matchmaking/MatchmakingNavStatus.vue';
import ModalWindow from '../ModalWindow.vue';
import SubscribeModal from '../SubscribeModal.vue';

export default {
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    CreditCardIcon,
    Bars3Icon,
    XMarkIcon,
    NavBar,
    ArrowRightOnRectangleIcon,
    AlertPopup,
    VideoCameraIcon,
    UserIcon,
    FilmIcon,
    Bars4Icon,
    ChartBarIcon,
    XCircleIcon,
    ChevronDownIcon,
    Menu,
    MenuItem,
    MenuItems,
    MenuButton,
    buttonLogo,
    SteamConnectModal,
    ChevronUpIcon,
    StarIcon,
    SmokeIcon,
    Pill,
    OnboardingPage,
    NotificationsPopup,
    RoutinesIcon,
    ModalWindow,
    SubscribeModal,
    CheckIcon,
    MatchmakingBlock,
    MatchmakingNavStatus,
  },
  setup() {
    // Preferences, game, team, mods, level-1 notifications, pings, matchmaking and presence
    const {
      activeTeam, selectedGame, loadSelectedGame, switchTeam, notifications, showNotifications,
      seeNotification, activeLobby, matchmakingStatus,
    } = useAppShell();

    const auth = useAuth();
    const sidebarOpen = ref(false);
    const route = useRoute();
    const router = useRouter();
    const routeName = computed(() => route.name);
    const routePath = computed(() => route.path);
    const sideBarExtended = ref(false);
    const showAccountNav = ref(false);
    const subMenuSliderMargin = ref(0);
    const showUpgradeModal = ref(false);
    const userSettings = ref(null);
    const gtm = useGtm();
    const { t } = useI18n();
    // Built as a function so the user() watcher can rebuild it when the
    // background /me lands — access_level-driven disabled/showUpgrade flags are
    // snapshotted at build time, so a cached boot must re-derive them on refresh.
    function buildNavigation() {
      return [
        {
          name: t('layout.nav.dashboard'),
          href: '/',
          icon: HomeIcon,
          current: routeName.value == 'Dashboard',
          customIcon: true,
        },
        {
          name: t('common.upgrade'),
          href: '/upgrade',
          icon: CreditCardIcon,
          current: routeName.value == 'UpgradePage',
          disabled: auth.user()?.access_level > 2,
        },
        {
          name: t('layout.nav.matchmaking'),
          key: 'matchmaking',
          href: '/matchmaking',
          icon: MatchmakingIcon,
          current: ['MatchmakingPage', 'MatchmakingLanPage', 'MatchmakingLobbyPage', 'LanHubPage', 'LanPartyPage',
            'LanControlRoomPage'].includes(routeName.value),
          customIcon: true,
          beta: true,
          disabled: !auth.user().can_use_matchmaking || !!auth.user().matchmaking_lan_only,
        },
        {
          name: t('layout.nav.routines'),
          href: '/routines',
          icon: RoutinesIcon,
          current: ['RoutinesPage', 'RoutinePage', 'CoachRoutinePage'].includes(routeName.value),
          customIcon: true,
          showUpgrade: auth.user()?.access_level < 1,
        },
        {
          name: t('layout.nav.coach'),
          href: '/coach',
          icon: CoachIcon,
          new: auth.user()?.access_level > 0,
          showUpgrade: auth.user()?.access_level < 1,
          current: routeName.value == 'CoachPage',
          customIcon: true,
        },
        {
          name: t('common.matches'),
          href: '/matches',
          icon: RestratIcon,
          current: routeName.value == 'MatchesPage',
          disabled: auth.user()?.access_level < 1,
          customIcon: true,
        },
        {
          name: t('layout.nav.sessions'),
          href: '/sessions',
          icon: TrainingSessionsIcon,
          current: routeName.value == 'TrainingSessionsPage',
          showUpgrade: auth.user()?.access_level < 1,
          beta: true,
          customIcon: true,
        },
        {
          name: t('layout.nav.demo_review'),
          href: '/demo_viewer_upgrade',
          icon: RestratIcon,
          current: routeName.value == 'DemoViewerUpgrade',
          disabled: auth.user()?.access_level > 0,
          showUpgrade: true,
          customIcon: true,
        },
        {
          name: t('layout.nav.library'),
          href: '/library',
          icon: LibraryIcon,
          current: routeName.value == 'LibraryPage',
          customIcon: true,
        },
        {
          name: t('layout.nav.community_hub'),
          href: '/community_hub/custom_sets',
          icon: GlobeAmericasIcon,
          current: routeName.value == 'CommunityHubPage',
          customIcon: true,
          subMenu: [
            {
              name: t('layout.nav.creator_studio'),
              href: '/community_hub/custom_sets',
              current: routePath.value == '/community_hub/custom_sets',
            },
            {
              name: t('layout.nav.nadr_grenade_groups'),
              href: '/community_hub/grenade_groups',
              current: routePath.value == '/community_hub/grenade_groups',
            },
            {
              name: t('layout.nav.nadr_strategies'),
              href: '/community_hub/strategies',
              current: routePath.value == '/community_hub/strategies',
            },
            {
              name: t('layout.nav.vpl'),
              href: '/community_hub/vpl',
              current: routePath.value == '/community_hub/vpl',
            },
          ],
        },
        {
          name: t('layout.nav.utility_hub'),
          href: '/utility_hub',
          icon: UtilityIcon,
          current: routeName.value == 'UtilityHubPage',
          showUpgrade: auth.user()?.access_level < 2,
          customIcon: true,
        },
        {
          name: t('layout.nav.leaderboards'),
          href: '/leaderboards',
          icon: LeaderboardIcon,
          current: routeName.value == 'Leaderboards',
          customIcon: true,
        },
        {
          name: t('layout.nav.academy'),
          href: '/academy',
          icon: AcademyIcon,
          showUpgrade: auth.user()?.access_level < 2,
          current: routeName.value == 'AcademyPage',
          customIcon: true,
        },
        {
          name: t('layout.nav.bootcamp'),
          href: '/bootcamp',
          icon: BootcampIcon,
          showUpgrade: auth.user()?.access_level < 1,
          current: routeName.value == 'BootcampsPage',
          customIcon: true,
        },
      ];
    }
    const navigation = ref(buildNavigation());

    // The party/lobby state behind matchmakingStatus is kept live by useAppShell().
    // null for every other nav item, so the sidebars can pass it unconditionally.
    function navStatus(item) {
      return item.key === 'matchmaking' ? matchmakingStatus.value : null;
    }

    function buildAccountNavigation() {
      return [
        {
          name: t('layout.nav.my_profile'),
          href: `/user/${getUserlink()}`,
          icon: UserIcon,
          current: routeName.value == 'UserProfilePage',
          disabled: auth.user()?.access_level < 1,
        },
        {
          name: t('layout.nav.my_team'),
          href: '/team',
          icon: UserGroupIcon,
          current: routeName.value == 'Team',
          disabled: auth.user()?.access_level < 3,
        },
        {
          name: t('common.settings'),
          href: '/settings',
          icon: CogIcon,
          current: routeName.value == 'SettingsPage',
        },
      ];
    }
    const accountNavigation = ref(buildAccountNavigation());

    const externalNavigation = [
      {
        name: t('layout.nav.how_to_use'),
        href: 'https://wiki.refrag.gg/',
        icon: WikiIcon,
        current: false,
        customIcon: true,
      },
      {
        name: t('layout.nav.support'),
        href: 'https://discord.gg/refrag',
        icon: SupportIcon,
        current: false,
        customIcon: true,
      },
    ];

    function getUserlink() {
      if (auth.user().username) {
        return auth.user().username;
      }
      return auth.user().uuid;
    }

    function toggleAccountNav() {
      showAccountNav.value = !showAccountNav.value;
    }

    function toggleSelectedGame(game) {
      selectedGame.value = game;
      updateSelectedGame();
    }

    function logout() {
      clearSignedOutUser();
      auth.logout({
        makeRequest: true,
        redirect: { name: 'Login' },
      });
    }

    function updateSelectedGame() {
      loadSelectedGame();
      window.location.reload();
    }

    function getTeamTierInfo(tier) {
      switch (tier) {
        case 1:
          return {
            text: t('layout.tier.player'),
            bgColor: '#8EBCE815',
            textColor: '#8EBCE8',
          };
        case 2:
          return {
            text: t('layout.tier.competitor'),
            bgColor: '#A94BFF15',
            textColor: '#A94BFF',
          };
        case 3:
          return {
            text: t('layout.tier.team'),
            bgColor: '#FBD50015',
            textColor: '#FBD500',
          };
        case 5:
          return {
            text: t('layout.tier.admin'),
            bgColor: '#EF6B6B15',
            textColor: '#EF6B6B',
          };
        default:
          return {
            text: t('layout.tier.free'),
            bgColor: '#B8C7D515',
            textColor: '#B8C7D5',
          };
      }
    }

    function getRoute(item) {
      if (item.disabled) {
        return '#';
      }
      // In a match, the matchmaking tab (and its play icon) goes to the
      // matchroom rather than the queue page you can no longer use.
      if (item.key === 'matchmaking' && activeLobby.value) {
        return { name: 'MatchmakingLobbyPage', params: { id: activeLobby.value.uuid } };
      }
      return item.href;
    }

    const updateActiveTeam = switchTeam;

    function toggleMobileMenu() {
      sidebarOpen.value = !sidebarOpen.value;
    }

    function updateSubMenuMargin(item) {
      if (!item.subMenu) return;
      item.subMenu.forEach((subItem, index) => {
        if (subItem.current) {
          subMenuSliderMargin.value = `${index * 32}px`;
        }
      });
    }

    watch(route, () => {
      navigation.value.forEach((item) => {
        if (item.subMenu && item.current) {
          item.subMenu.forEach((subItem) => {
            subItem.current = routePath.value == subItem.href;
            updateSubMenuMargin(item);
          });
        }
      });
    });

    navigation.value.forEach((item) => {
      if (item.subMenu && item.current) {
        updateSubMenuMargin(item);
      }
    });

    // When we boot from cached /me, the live /me lands a moment later and
    // replaces the user object. Re-derive the access_level-driven sidebar flags
    // snapshotted at setup (useAppShell re-derives team and matchmaking state).
    watch(
      () => auth.user(),
      (user) => {
        // vue-auth nulls the user on logout/401 — skip the rebuilds then, since
        // buildNavigation() reads access_level off the user and would throw
        // mid-teardown/redirect.
        if (!user) return;
        navigation.value = buildNavigation();
        accountNavigation.value = buildAccountNavigation();
      },
    );

    return {
      navigation,
      sidebarOpen,
      Logo,
      buttonLogo,
      logout,
      updateActiveTeam,
      externalNavigation,
      activeTeam,
      sideBarExtended,
      showAccountNav,
      toggleAccountNav,
      accountNavigation,
      toggleSelectedGame,
      selectedGame,
      getRoute,
      navStatus,
      toggleMobileMenu,
      subMenuSliderMargin,
      notifications,
      showNotifications,
      seeNotification,
      showUpgradeModal,
      getTeamTierInfo,
      router,
    };
  },
  data() {
    return {
      showSteamConnectModal: false,
    };
  },
  props: ['title', 'dark', 'showSteamLogin', 'iconsOnly', 'noPadding', 'pinProfileSidebar', 'skipOnboarding'],
  computed: {
    user() {
      return this.$auth.user() || {};
    },
    username() {
      if (this.user.username) {
        return this.user.username;
      } if (this.user.steam_profile) {
        return this.user.steam_profile.personaname;
      }
      return this.user.email;
    },
    userCheckListProgress() {
      return [
        {
          step: 1,
          title: this.$t('layout.checklist.auth_code_title'),
          description: this.$t('layout.checklist.auth_code_description'),
          completed: this.user.has_match_auth_token,
          route: '/settings',
        },
        {
          step: 2,
          title: this.$t('layout.checklist.match_token_title'),
          description: this.$t('layout.checklist.match_token_description'),
          completed: !this.user.match_token_outdated,
          route: '/settings',
        },
        {
          step: 3,
          title: this.$t('layout.checklist.steam_bot_title'),
          description: this.$t('layout.checklist.steam_bot_description'),
          completed: this.user.has_steam_bot_added,
          route: '/settings',
        },
        {
          step: 4,
          title: this.$t('layout.checklist.faceit_title'),
          description: this.$t('layout.checklist.faceit_description'),
          completed: this.user.faceit_nickname,
          route: '/settings',
        },
        {
          step: 5,
          title: this.$t('layout.checklist.discord_title'),
          description: this.$t('layout.checklist.discord_description'),
          completed: this.user.has_discord_linked,
          route: '/settings',
        },
      ];
    },
    userCheckListCompleted() {
      return this.userCheckListProgress.every((item) => item.completed);
    },
    userCompletedTasks() {
      return this.userCheckListProgress.filter((item) => item.completed);
    },
    userUncompletedTasks() {
      return this.userCheckListProgress.filter((item) => !item.completed);
    },
    imageURL() {
      if (this.user.steam_profile) {
        return this.user.steam_profile.avatar;
      }
      return buttonLogo;
    },
  },
  created() {
    if (!this.user.steam_id) this.showSteamConnectModal = true;
    if (this.showSteamLogin !== undefined) this.showSteamConnectModal = this.showSteamLogin;
  },
};
</script>

<style scoped>
.selected-nav {
  box-shadow: 2px 0 0 #4E54C8 inset;
}

.grow-transition {
  transition: width .3s;
}

.fadeHeight-enter-active,
.fadeHeight-leave-active {
  max-height: 230px;
  transition: all 0.15s ease-in;
}
.fadeHeight-enter-from,
.fadeHeight-leave-to
{
  opacity: 0;
  max-height: 0px;
  transition: all 0.15s ease-in-out;
}

.boxshadow-r {
  box-shadow:  1px 0px 0px 0px #222C39;
}

.progress-ring__circle {
  stroke-dasharray: 251.33;
  transition: stroke-dashoffset 0.35s;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

</style>
